// @tennis-matches/services/i18n
// i18n client logic
// Migrated from src/i18n/i18n.js

let REMOTE_MESSAGES = {};
let REMOTE_MESSAGES_VERSION = 0; // Increment to trigger re-renders
let __remoteMessagesFingerprint = "";

function __isI18nDebugEnabled() {
  try {
    return (
      typeof import.meta !== "undefined" &&
      !!import.meta.env?.DEV &&
      typeof localStorage !== "undefined" &&
      localStorage.getItem("tm_debug_i18n") === "1"
    );
  } catch {
    return false;
  }
}

function __computeRemoteMessagesFingerprint(messages) {
  if (!messages || typeof messages !== "object") return "0";
  const keys = Object.keys(messages);
  const n = keys.length;
  if (n === 0) return "0";
  const samples = ["title.app", "nav.settings", "nav.schleifchen"]
    .map((k) => `${k}=${messages[k] ?? ""}`)
    .join("|");
  return `${n}:${samples}`;
}

export function setRemoteMessages(messages) {
  const fp = __computeRemoteMessagesFingerprint(messages);
  if (fp === __remoteMessagesFingerprint && REMOTE_MESSAGES_VERSION > 0) {
    return REMOTE_MESSAGES_VERSION;
  }
  __remoteMessagesFingerprint = fp;
  REMOTE_MESSAGES = { ...messages };
  REMOTE_MESSAGES_VERSION++;
  const count = Object.keys(messages || {}).length;
  if (__isI18nDebugEnabled()) {
    console.log(
      "[i18n@services] setRemoteMessages called with",
      count,
      "messages (version:",
      REMOTE_MESSAGES_VERSION,
      ")"
    );
    const sampleKeys = ["title.app", "nav.schleifchen", "nav.settings", "schleifchen.title", "timer.title"].filter(
      (k) => messages && messages[k]
    );
    if (sampleKeys.length > 0) {
      console.log(
        "[i18n@services] Sample keys in REMOTE_MESSAGES:",
        sampleKeys.map((k) => `${k}="${messages[k]}"`).join(", ")
      );
    }
  }

  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("i18n-messages-updated", {
        detail: { version: REMOTE_MESSAGES_VERSION, count },
      })
    );
  }
  return REMOTE_MESSAGES_VERSION;
}

export function getRemoteMessagesVersion() {
  return REMOTE_MESSAGES_VERSION;
}

import de from "./locales/de.js";

/** Lokale Fallback-Wörterbücher (primär: DB via setRemoteMessages). */
const MESSAGES = {
  de,
};

// --- locale resolution -------------------------------------------------

function normalizeLocale(locale) {
  const s = String(locale || "").trim();
  if (!s) return "de";
  // unify underscore and casing: de_at -> de-AT
  const parts = s.replaceAll("_", "-").split("-").filter(Boolean);
  if (!parts.length) return "de";
  const lang = parts[0].toLowerCase();
  const region = parts[1] ? parts[1].toUpperCase() : "";
  return region ? `${lang}-${region}` : lang;
}

function resolveLocale(locale) {
  const loc = normalizeLocale(locale);

  // exact match
  if (MESSAGES[loc]) return loc;

  // language-only fallback: de-DE -> de
  const lang = loc.split("-")[0];
  if (MESSAGES[lang]) return lang;

  // default
  return MESSAGES.de ? "de" : Object.keys(MESSAGES)[0] || "de";
}

// --- formatting --------------------------------------------------------

function formatVars(str, vars) {
  if (!vars) return String(str);
  let out = String(str);
  // {{key}} — in Locale-Dateien üblich; zuerst ersetzen, damit keine Rest-Klammern bleiben
  out = out.replace(/\{\{(\w+)\}\}/g, (_, k) =>
    Object.prototype.hasOwnProperty.call(vars, k) ? String(vars[k]) : `{{${k}}}`
  );
  // {key} — älteres Format
  out = out.replace(/\{(\w+)\}/g, (_, k) =>
    Object.prototype.hasOwnProperty.call(vars, k) ? String(vars[k]) : `{${k}}`
  );
  return out;
}

// Optional: minimal plural helper (supports objects like { one:"", other:"" })
function pickPlural(raw, vars) {
  if (!raw || typeof raw !== "object") return raw;
  const n = Number(vars?.count);
  const key = n === 1 ? "one" : "other";
  return raw[key] ?? raw.other ?? raw.one;
}

// --- translation core --------------------------------------------------
// Keep as internal helper to avoid accidental misuse elsewhere.
function translateCore(locale, key, vars) {
  const loc = resolveLocale(locale);
  const dict = MESSAGES[loc] || {};
  const fb = MESSAGES.de || {};

  // PRIORITY 1: DB-Texten (höchste Priorität - kann lokal überschrieben werden)
  // PRIORITY 2: Lokale Dateien (Fallback für Offline/Initial Load)
  // PRIORITY 3: Key selbst (letzter Fallback)
  // Always access REMOTE_MESSAGES directly (module-level variable, always current)
  let raw = REMOTE_MESSAGES[key];
  const source = raw != null ? 'REMOTE' : (dict[key] != null ? 'LOCAL' : (fb[key] != null ? 'FALLBACK' : 'KEY'));
  
  if (raw == null) raw = dict[key];
  if (raw == null) raw = fb[key];
  if (raw == null) raw = key; // ultimate fallback

  // Debug logging for specific keys (opt-in: localStorage tm_debug_i18n=1)
  const importantKeys = [
    "title.app",
    "nav.schleifchen",
    "nav.settings",
    "schleifchen.title",
    "timer.title",
    "timer.startRound",
    "common.vs",
    "SCHEDULE.COURTPREFIX",
  ];
  if (__isI18nDebugEnabled() && importantKeys.includes(key)) {
    console.log(
      `[i18n@services] translateCore("${key}") → "${raw}" (source: ${source}, REMOTE_MESSAGES has ${Object.keys(REMOTE_MESSAGES).length} keys, version: ${REMOTE_MESSAGES_VERSION})`
    );
    if (source === "KEY" && REMOTE_MESSAGES_VERSION > 0) {
      console.warn(
        `[i18n@services] ⚠️ Key "${key}" not found in REMOTE_MESSAGES! Available keys:`,
        Object.keys(REMOTE_MESSAGES).slice(0, 20).join(", ")
      );
    }
  }

  raw = pickPlural(raw, vars);
  return formatVars(raw, vars);
}

/**
 * Returns a translator function bound to a locale.
 * Usage: const t = tt(state.app.locale); t("nav.timer")
 * 
 * IMPORTANT: The returned function always accesses REMOTE_MESSAGES directly
 * (module-level variable), so it will always use the latest messages even
 * if the function was created before messages were loaded.
 */
export function tt(locale) {
  const loc = resolveLocale(locale);

  function t(key, vars) {
    // Always access REMOTE_MESSAGES directly (module-level, always current)
    // This ensures the function works even if created before messages are loaded
    return translateCore(loc, key, vars);
  }

  // Helpful metadata for debugging/logging
  t.locale = loc;
  t.__isBoundTranslator = true;
  // Store version to help with debugging
  t.__messagesVersion = REMOTE_MESSAGES_VERSION;

  return t;
}

/**
 * Optional helper: list available locales
 */
export function getAvailableLocales() {
  return Object.keys(MESSAGES);
}
